import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { editKyc, profileDetails, sendUpdateOtp, verifyUpdateOtp } from "../../services/user.service";
import * as UserServices from "../../../user/services/user.service";
import { useDispatch } from "react-redux";
import { addProfileData } from "../../../../redux/slices/profileslice";
import { SWAL_SETTINGS, ROLE } from "../../../../utils/Constants";
import { useNavigate } from "react-router-dom";
import { handleServerValidations, getUser, setLocalKey } from "../../../../utils/commonfunction";
import PhoneInput from "../../../../utils/PhoneInput";

const getCleanDigits = (val) => String(val || "").replace(/\D/g, "").slice(-10);

const FileLink = ({ name, url }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-gray-600 hover:text-red-500 transition duration-150">
    <img src="/images/pdf.svg" />
    <span className="text-[15px] text-zinc-500">{name}</span>
  </a>
);

function KYCInformation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdvertiserContext = window.location.href.includes("/advertiser");
  const fallbackRole = isAdvertiserContext ? ROLE.ADVERTISER : ROLE.USER;
  let userData = getUser(ROLE.USER);
  if (!userData || Object.keys(userData).length === 0) {
    userData = getUser(ROLE.ADVERTISER) || {};
  }
  const modalRef = useRef();
  const formikRef = useRef();

  const dispatch = useDispatch();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isUserFlow = userData && userData.token && userData.role === ROLE.USER;
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [verifiedKycNumber, setVerifiedKycNumber] = useState(() => {
    const initialUser = getUser(ROLE.USER) || getUser(ROLE.ADVERTISER) || {};
    if (initialUser?.is_kyc_phone_verified) {
      return getCleanDigits(initialUser.kyc_mobile_number);
    }
    return "";
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await profileDetails();
        if (res?.data) {
          setProfileData(res.data);
          if (res.data.is_kyc_phone_verified) {
            setVerifiedKycNumber(getCleanDigits(res.data.kyc_mobile_number));
          }
          const existingUser = getUser(ROLE.USER) || {};
          const mergedData = { ...existingUser, ...res.data };
          dispatch(addProfileData(mergedData));
          setLocalKey(ROLE.USER, JSON.stringify(mergedData));
          setLocalKey(ROLE.ADVERTISER, JSON.stringify(mergedData));
        }
      } catch (err) {
        console.error("Failed to fetch fresh KYC profile:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [dispatch, fallbackRole]);

  const displayData = profileData || userData;

  const initialValues = {
    kyc_name: displayData.kyc_name || displayData.name || "",
    kyc_dob: displayData.kyc_dob ? displayData.kyc_dob.split("T")[0] : "",
    kyc_gender: displayData.kyc_gender || "",
    kyc_mobile_number: displayData.kyc_mobile_number || "",
    kyc_doc_type: displayData.kyc_doc_type || "aadhar",
    kyc_doc_number: displayData.kyc_doc_number || "",
    kyc_doc_front: displayData.kyc_doc_front,
    kyc_doc_back: displayData.kyc_doc_back,
    kyc_social_media: displayData.kyc_social_media || [],
  };

  const validationSchema = Yup.object({
    kyc_name: Yup.string().required("Name is required"),
    kyc_dob: Yup.string()
      .required("DOB is required")
      .test("not-in-future", "DOB cannot be in the future", function (value) {
        if (!value) return false;

        const selectedDate = new Date(value);
        const today = new Date();

        // Remove time from today
        today.setHours(0, 0, 0, 0);

        return selectedDate <= today;
      })
      .test("age", "You must be at least 18 years old", function (value) {
        if (!value) return false;

        const today = new Date();
        const birthDate = new Date(value);

        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        return age >= 18;
      }),

    kyc_gender: Yup.string().required("Gender is required"),
    kyc_mobile_number: Yup.string().required("Mobile number is required"),
    kyc_doc_type: Yup.string().required("Document type is required"),
    kyc_doc_number: Yup.string()
      .required("Document number is required")
      .test("valid-doc", "Invalid document number", function (value) {
        const { kyc_doc_type } = this.parent;
        if (!value) return false;
        if (kyc_doc_type === "aadhar") {
          return /^\d{12}$/.test(value);
        } else if (kyc_doc_type === "pan") {
          return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase());
        } else if (kyc_doc_type === "passport") {
          return /^[A-Z]{1}[0-9]{7}$/.test(value.toUpperCase());
        }
        return true;
      }),
  });

  const getSwalTarget = () => {
    const dialog = document.getElementById("kyc-update");
    return dialog && dialog.open ? dialog : "body";
  };

  const doUpdateKyc = async (values) => {
    try {
      const formData = new FormData();

      formData.append("kyc_name", values.kyc_name);
      formData.append("kyc_dob", values.kyc_dob);
      formData.append("kyc_gender", values.kyc_gender);
      formData.append("kyc_mobile_number", values.kyc_mobile_number);
      formData.append("kyc_doc_type", values.kyc_doc_type);
      formData.append("kyc_doc_number", values.kyc_doc_number);

      // Social media as array → backend accepts JSON string also
      formData.append("kyc_social_media", JSON.stringify(values.kyc_social_media));

      if (values.kyc_doc_front instanceof File) {
        formData.append("kyc_doc_front", values.kyc_doc_front);
      }
      if (values.kyc_doc_back instanceof File) {
        formData.append("kyc_doc_back", values.kyc_doc_back);
      }

      const response = await editKyc(formData);
      if (response.success) {
        document.getElementById("kyc-update")?.close();
        modalRef.current?.click();
        Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
        const freshRes = await profileDetails();
        if (freshRes?.data) {
          const existingUser = getUser(ROLE.USER) || {};
          const mergedData = { ...existingUser, ...freshRes.data };
          dispatch(addProfileData(mergedData));
          setLocalKey(ROLE.USER, JSON.stringify(mergedData));
          setLocalKey(ROLE.ADVERTISER, JSON.stringify(mergedData));
          setProfileData(mergedData);
        }
      } else {
        Swal.fire({
          ...SWAL_SETTINGS,
          target: getSwalTarget(),
          icon: "error",
          text: handleServerValidations(response),
        });
      }
    } catch (err) {
      console.log(err);
      Swal.fire({
        ...SWAL_SETTINGS,
        target: getSwalTarget(),
        icon: "error",
        text: "Failed to update KYC details.",
      });
    }
  };

  const handleSendOtp = async (phoneNumber, autoSubmitAfterVerify = false) => {
    const cleanPhone = getCleanDigits(phoneNumber);
    if (!cleanPhone || cleanPhone.length !== 10) {
      Swal.fire({
        ...SWAL_SETTINGS,
        target: getSwalTarget(),
        icon: "error",
        text: "Please enter a valid 10-digit mobile number.",
      });
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const payload = {
        _id: displayData._id,
        phone_number: cleanPhone,
        type: "kyc_phone",
      };

      const response = isUserFlow
        ? await UserServices.sendUpdateOtp(payload)
        : await sendUpdateOtp(payload);

      if (response && response.success) {
        const { value: otp } = await Swal.fire({
          target: getSwalTarget(),
          customClass: {
            container: "!z-[99999]",
          },
          title: "Verify Mobile Number",
          text: `Enter the 6-digit OTP sent to +91 ${cleanPhone}`,
          input: "text",
          inputLabel: "Enter 6-digit OTP",
          inputPlaceholder: "123456",
          showCancelButton: true,
          confirmButtonText: "Verify",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#f43f5e",
          cancelButtonColor: "#6b7280",
          inputAttributes: {
            maxlength: 6,
            autocapitalize: "off",
            autocorrect: "off",
            style: "text-align: center; letter-spacing: 4px; font-size: 1.25rem; font-weight: 600;",
          },
          inputValidator: (value) => {
            if (!value || value.length !== 6 || !/^\d+$/.test(value)) {
              return "Please enter a valid 6-digit OTP";
            }
          },
        });

        if (otp) {
          await handleVerifyOtp(cleanPhone, otp, autoSubmitAfterVerify);
        }
      } else {
        Swal.fire({
          ...SWAL_SETTINGS,
          target: getSwalTarget(),
          icon: "error",
          text: handleServerValidations(response) || "Failed to send OTP",
        });
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        ...SWAL_SETTINGS,
        target: getSwalTarget(),
        icon: "error",
        text: "Something went wrong while sending OTP.",
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleVerifyOtp = async (phoneNumber, otp, autoSubmitAfterVerify = false) => {
    try {
      const payload = {
        _id: displayData._id,
        phoneOtp: otp,
        type: "kyc_phone",
      };

      const response = isUserFlow
        ? await UserServices.verifyUpdateOtp(payload)
        : await verifyUpdateOtp(payload);

      if (response && response.success) {
        setVerifiedKycNumber(phoneNumber);
        const updatedUser = {
          ...displayData,
          kyc_mobile_number: `+91 ${phoneNumber}`,
          is_kyc_phone_verified: true,
        };
        setProfileData(updatedUser);
        if (isUserFlow) {
          setLocalKey(ROLE.USER, JSON.stringify(updatedUser));
        } else {
          setLocalKey(ROLE.ADVERTISER, JSON.stringify(updatedUser));
        }
        dispatch(addProfileData(updatedUser));

        Swal.fire({
          ...SWAL_SETTINGS,
          target: getSwalTarget(),
          icon: "success",
          text: "KYC mobile number verified successfully!",
        });

        if (autoSubmitAfterVerify && formikRef.current) {
          await doUpdateKyc({
            ...formikRef.current.values,
            kyc_mobile_number: `+91 ${phoneNumber}`,
          });
        }
      } else {
        Swal.fire({
          ...SWAL_SETTINGS,
          target: getSwalTarget(),
          icon: "error",
          text: handleServerValidations(response) || "Invalid OTP",
        });
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        ...SWAL_SETTINGS,
        target: getSwalTarget(),
        icon: "error",
        text: "Something went wrong during verification.",
      });
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Verification check bypassed for now - proceed even if unverified
      /*
      const cleanInput = getCleanDigits(values.kyc_mobile_number);
      const isVerified = Boolean(
        cleanInput &&
        cleanInput.length === 10 &&
        verifiedKycNumber &&
        cleanInput === verifiedKycNumber
      );

      if (!isVerified) {
        handleSendOtp(values.kyc_mobile_number, true);
        return;
      }
      */

      await doUpdateKyc(values);
    } catch (error) {
      handleServerValidations(error, Swal);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestUpdate = () => {
    const isAdvertiser = Boolean(
      displayData?.isAdvertiser === true ||
      displayData?.isAdvertiser === "true" ||
      userData?.isAdvertiser === true ||
      userData?.isAdvertiser === "true" ||
      profileData?.isAdvertiser === true ||
      profileData?.isAdvertiser === "true" 
    );

    if (isAdvertiser) {
      Swal.fire({
        icon: "info",
        title: t("advertiser.profile.contact_admin_title", "Contact Administrator"),
        text: t(
          "advertiser.profile.contact_admin_desc",
          "Please contact the administrator to update your KYC details."
        ),
        confirmButtonText: t("advertiser.profile.contact_admin_btn", "Contact Us"),
        confirmButtonColor: "#f26a61",
        showCancelButton: true,
        cancelButtonText: t("common.cancel", "Cancel"),
        cancelButtonColor: "#6B7280",
        timer: 5000,
        timerProgressBar: true,
      }).then((result) => {
        if (result.isConfirmed || result.dismiss === Swal.DismissReason.timer) {
          navigate("/contact-us");
        }
      });
      return;
    }

    document.getElementById("kyc-update")?.showModal();
  };

  if (loading) return <div className="p-10 text-center">{t('advertiser.invoice.loading')}</div>;

  return (
    <>
      <div className="w-full space-y-6">
        {displayData?.failed_verification_reason && !displayData?.is_kyc_verified && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r">
            <div className="flex items-start">
              <div className="ml-1">
                <h3 className="text-sm font-bold text-red-800">
                  Advertiser Verification Rejected
                </h3>
                <div className="mt-1 text-sm text-red-700">
                  <p><strong>Reason:</strong> {displayData.failed_verification_reason}</p>
                </div>
                <p className="mt-1 text-xs text-red-600">Click &quot;Update KYC&quot; below to correct your details and re-submit for review.</p>
              </div>
            </div>
          </div>
        )}
        {/* Personal Section */}
        <div>
          <h2 className="headings-h5 text-zinc-950 mb-3">{t('advertiser.profile.personal_info')}</h2>
          <div className="bg-white p-[15px] rounded-[10px] shadow border">
            <p className="headings-h5 text-zinc-950">{displayData.kyc_name || displayData.name}</p>
            <p className="text-[15px] text-zinc-500">{displayData.email}</p>
            <p className="text-[15px] text-zinc-500">{displayData.kyc_mobile_number || displayData.phone_number}</p>
          </div>
        </div>

        {/* Identity Section */}
        <div>
          <h2 className="headings-h5 text-zinc-950 mb-3">{t('advertiser.profile.identity_verification')}</h2>
          <div className="bg-white p-[15px] rounded-[10px] shadow border">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="headings-h6 text-zinc-950">{displayData.kyc_doc_type?.toUpperCase()}</h3>
              {displayData.kyc_doc_type?.toLowerCase() === "pan" && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200 rounded">
                  Business Proof
                </span>
              )}
              {(displayData.kyc_doc_type?.toLowerCase() === "aadhar" || displayData.kyc_doc_type?.toLowerCase() === "aadhaar") && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 rounded">
                  Address Proof
                </span>
              )}
            </div>
            <p className="text-[15px] text-zinc-500 mb-4">{displayData.kyc_doc_number}</p>

            <div className="flex gap-4 flex-wrap">
              {displayData.kyc_doc_front && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-zinc-600">{t('advertiser.profile.front', 'Front')}</span>
                    {displayData.kyc_doc_type?.toLowerCase() === "pan" && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200 rounded">
                        Business Proof
                      </span>
                    )}
                    {(displayData.kyc_doc_type?.toLowerCase() === "aadhar" || displayData.kyc_doc_type?.toLowerCase() === "aadhaar") && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 rounded">
                        Address Proof
                      </span>
                    )}
                  </div>
                  <a
                    href={displayData.kyc_doc_front}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-[150px] h-[120px] border rounded-md overflow-hidden block"
                  >
                    <img
                      src={displayData.kyc_doc_front}
                      alt="Front"
                      className="w-full h-full object-cover"
                    />
                  </a>
                </div>
              )}

              {displayData.kyc_doc_back && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-zinc-600">{t('advertiser.profile.back', 'Back')}</span>
                    {displayData.kyc_doc_type?.toLowerCase() === "pan" && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200 rounded">
                        Business Proof
                      </span>
                    )}
                    {(displayData.kyc_doc_type?.toLowerCase() === "aadhar" || displayData.kyc_doc_type?.toLowerCase() === "aadhaar") && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 rounded">
                        Address Proof
                      </span>
                    )}
                  </div>
                  <a
                    href={displayData.kyc_doc_back}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-[150px] h-[120px] border rounded-md overflow-hidden block"
                  >
                    <img
                      src={displayData.kyc_doc_back}
                      alt="Back"
                      className="w-full h-full object-cover"
                    />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Update Button */}
        <button className="btn btn-pri mt-4" onClick={handleRequestUpdate}>
          {t('advertiser.profile.update_details', 'Update Details')}
        </button>
      </div>

      {/* Modal */}
      <dialog id="kyc-update" className="modal p-[15px]">
        <div className="modal-box max-w-[645px] !w-full">
          <h2 className="headings-h1 text-left text-zinc-800 mb-4">{t('advertiser.profile.update_details', 'Update Details')}</h2>

          <Formik enableReinitialize innerRef={formikRef} initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {({ values, setFieldValue, isSubmitting }) => (
              <Form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[30px]">
                  {/* Name */}
                  <div className="form-group">
                    <label>{t('advertiser.my_team.name')}</label>
                    <Field type="text" name="kyc_name" className="form-control-custom" />
                    <ErrorMessage name="kyc_name" component="div" className="text-red-500 text-sm" />
                  </div>

                  {/* DOB */}
                  <div className="form-group">
                    <label>{t('advertiser.profile.dob')}</label>
                    <Field
                      type="date"
                      name="kyc_dob"
                      max={new Date().toISOString().split("T")[0]}
                      className="form-control-custom"
                    />
                    <ErrorMessage name="kyc_dob" component="div" className="text-red-500 text-sm" />
                  </div>

                  {/* Gender */}
                  <div className="form-group">
                    <label>{t('advertiser.profile.gender')}</label>
                    <Field as="select" name="kyc_gender" className="form-control-custom">
                      <option value="">{t('advertiser.profile.select_gender')}</option>
                      <option value="male">{t('advertiser.profile.male')}</option>
                      <option value="female">{t('advertiser.profile.female')}</option>
                    </Field>
                    <ErrorMessage name="kyc_gender" component="div" className="text-red-500 text-sm" />
                  </div>

                  {/* Mobile */}
                  <div className="form-group">
                    {(() => {
                      const cleanInput = getCleanDigits(values.kyc_mobile_number);
                      const isVerified = Boolean(
                        cleanInput &&
                        cleanInput.length === 10 &&
                        verifiedKycNumber &&
                        cleanInput === verifiedKycNumber
                      );

                      return (
                        <div className="flex justify-between items-end mb-2">
                          <label className="mb-0">{t('advertiser.profile.mobile_number')}</label>
                          {isVerified ? (
                            <span className="text-green-500 font-medium text-sm flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                              </svg>
                              Verified
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="text-primary-500 text-sm font-medium hover:underline focus:outline-none"
                              onClick={() => handleSendOtp(values.kyc_mobile_number)}
                              disabled={isVerifyingOtp}
                            >
                              {isVerifyingOtp ? "Sending..." : "Verify Now"}
                            </button>
                          )}
                        </div>
                      );
                    })()}

                    <PhoneInput
                      value={values.kyc_mobile_number}
                      onChange={(val) => {
                        const finalValue = typeof val === "object" ? `${val.code} ${val.number}` : val;

                        setFieldValue("kyc_mobile_number", finalValue);
                      }}
                    />

                    <ErrorMessage name="kyc_mobile_number" component="div" className="text-red-500 text-sm" />
                  </div>

                  {/* Doc Type */}
                  <div className="form-group">
                    <label>{t('advertiser.profile.document_type')}</label>
                    <Field as="select" name="kyc_doc_type" className="form-control-custom">
                      <option value="aadhar">{t('advertiser.profile.aadhar', 'Aadhar Card')}</option>
                      <option value="pan">PAN</option>
                      <option value="driving_license">{t('advertiser.profile.driving_license', 'Driving License')}</option>
                      <option value="passport">{t('advertiser.profile.passport', 'Passport')}</option>
                    </Field>
                  </div>

                  {/* Doc Number */}
                  <div className="form-group">
                    <label>{t('advertiser.profile.document_number')}</label>
                    <input
                      type="text"
                      name="kyc_doc_number"
                      value={values.kyc_doc_number}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (values.kyc_doc_type === "pan") {
                          val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
                        } else if (values.kyc_doc_type === "aadhar") {
                          val = val.replace(/[^0-9]/g, "");
                        } else if (values.kyc_doc_type === "passport") {
                          val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
                        }
                        setFieldValue("kyc_doc_number", val);
                      }}
                      className="form-control-custom"
                      placeholder={
                        values.kyc_doc_type === "aadhar"
                          ? "123456789012"
                          : values.kyc_doc_type === "pan"
                            ? "ABCDE1234F"
                            : values.kyc_doc_type === "passport"
                              ? "A1234567"
                              : "Document Number"
                      }
                      maxLength={
                        values.kyc_doc_type === "aadhar"
                          ? 12
                          : values.kyc_doc_type === "pan"
                            ? 10
                            : values.kyc_doc_type === "passport"
                              ? 8
                              : 20
                      }
                      style={
                        values.kyc_doc_type === "pan" || values.kyc_doc_type === "passport"
                          ? { textTransform: "uppercase" }
                          : {}
                      }
                    />
                    <ErrorMessage name="kyc_doc_number" component="div" className="text-red-500 text-sm mt-1" />
                  </div>
                </div>

                {/* File Upload */}
                <div className="form-group mt-3">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <label className="mb-0">
                      {t('advertiser.profile.upload_photo')} {values.kyc_doc_type === "aadhar" ? t('advertiser.profile.aadhar') : values.kyc_doc_type === "pan" ? "PAN" : values.kyc_doc_type === "passport" ? t('advertiser.profile.passport') : values.kyc_doc_type === "driving_license" ? t('advertiser.profile.driving_license') : t('advertiser.profile.document_type')}
                    </label>
                    {values.kyc_doc_type?.toLowerCase() === "pan" && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200 rounded">
                        Business Proof
                      </span>
                    )}
                    {(values.kyc_doc_type?.toLowerCase() === "aadhar" || values.kyc_doc_type?.toLowerCase() === "aadhaar") && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 rounded">
                        Address Proof
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-[15px] flex-wrap">
                    {/* FRONT BOX */}
                    <div className="flex flex-col gap-1">
                      <div
                        className="w-[122px] h-[122px] bg-primary-50 border-dashed border-primary-400 
                   flex flex-col items-center justify-center rounded-[8px] cursor-pointer overflow-hidden relative"
                        onClick={() => document.getElementById("frontFile").click()}>
                        {/* If preview or already saved file → show it */}
                        {values.kyc_doc_front && typeof values.kyc_doc_front === "string" ? (
                          <img src={values.kyc_doc_front} className="w-full h-full object-cover" />
                        ) : values.kyc_doc_front && typeof values.kyc_doc_front === "object" ? (
                          <img src={URL.createObjectURL(values.kyc_doc_front)} className="w-full h-full object-cover" />
                        ) : (
                          <>
                            <img src="/images/upload2.svg" alt="upload" />
                            <p className="body-1 text-primary-400">{t('advertiser.profile.front')}</p>
                          </>
                        )}
                      </div>
                      <span className="text-[11px] text-center text-zinc-500 font-medium">{t('advertiser.profile.front', 'Front')}</span>
                    </div>

                    <input id="frontFile" type="file" className="hidden" accept="image/*" onChange={(e) => setFieldValue("kyc_doc_front", e.target.files[0])} />

                    {/* BACK BOX */}
                    <div className="flex flex-col gap-1">
                      <div
                        className="w-[122px] h-[122px] bg-primary-50 border-dashed border-primary-400 
                   flex flex-col items-center justify-center rounded-[8px] cursor-pointer overflow-hidden relative"
                        onClick={() => document.getElementById("backFile").click()}>
                        {/* Existing or new preview */}
                        {values.kyc_doc_back && typeof values.kyc_doc_back === "string" ? (
                          <img src={values.kyc_doc_back} className="w-full h-full object-cover" />
                        ) : values.kyc_doc_back && typeof values.kyc_doc_back === "object" ? (
                          <img src={URL.createObjectURL(values.kyc_doc_back)} className="w-full h-full object-cover" />
                        ) : (
                          <>
                            <img src="/images/upload2.svg" alt="upload" />
                            <p className="body-1 text-primary-400">{t('advertiser.profile.back')}</p>
                          </>
                        )}
                      </div>
                      <span className="text-[11px] text-center text-zinc-500 font-medium">{t('advertiser.profile.back', 'Back')}</span>
                    </div>

                    <input id="backFile" type="file" className="hidden" accept="image/*" onChange={(e) => setFieldValue("kyc_doc_back", e.target.files[0])} />
                  </div>
                </div>

                {/* Social Media */}
                {/* <div className="form-group mt-4">
                  <label>{t('advertiser.profile.social_links')}</label>

                  <FieldArray name="kyc_social_media">
                    {({ push, remove }) => (
                      <>
                        {values.kyc_social_media.map((item, index) => (
                          <div key={index} className="flex items-center gap-[15px] mt-2">
                            <Field type="text" name={`kyc_social_media.${index}`} placeholder="https://www.facebook.com/" className="form-control-custom flex-1" />
                            <button type="button" onClick={() => remove(index)} className="w-[44px] h-[44px] bg-primary-400 rounded-full flex items-center justify-center">
                              <img src="/images/cross.png" alt="cross" />
                            </button>
                          </div>
                        ))}

                        <button type="button" onClick={() => push("")} className="add-more-btn headings-h5 text-secondary-500 mb-3">
                          {t('advertiser.invoice.add_more')}
                        </button>
                      </>
                    )}
                  </FieldArray>
                </div> */}

                {/* Submit */}
                <button type="submit" disabled={isSubmitting || isVerifyingOtp} className="btn btn-pri w-full">
                  {isSubmitting ? t('advertiser.profile.updating') : t('advertiser.profile.update')}
                </button>
              </Form>
            )}
          </Formik>
        </div>

        <form method="dialog">
          <button
            type="button"
            onClick={() => {
              formikRef.current?.resetForm();
              document.getElementById("kyc-update").close();
            }}
            className="bg-white subtitle-2 flex items-center gap-[5px] px-[15px] py-[10px] rounded-full"
            ref={modalRef}
          >
            <img src="/images/close.svg" alt="close" />
            <span className="subtitle-2 bold">{t('advertiser.my_team.close')}</span>
          </button>
        </form>

      </dialog>
    </>
  );
}

export default KYCInformation;
